import { useState, useEffect } from 'react';
import { ArrowLeft, X, ChevronRight, Copy, Check, Loader2 } from 'lucide-react';
import { useAppState } from '../../context/AppStateContext';
import BillingFlow from '../billing/BillingFlow';
import PayBillSlider from '../shop/PayBillSlider';
import { groupsApi, groupCodeApi, providersApi, servicesApi, marketplaceDealsApi } from '../../services/api';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

export default function GroupDealsDrawer() {
  const {
    selectedGroup,
    showGroupDealsDrawer,
    setShowGroupDealsDrawer,
    isolatedGroupDeals,
  } = useAppState();

  const [billingProvider, setBillingProvider] = useState(null);
  const [groupDetails, setGroupDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Code generation state
  const [showCodeSlider, setShowCodeSlider] = useState(false);
  const [generatedCode, setGeneratedCode] = useState(null);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);

  // Provider matching state for PayBillSlider
  const [matchedProvider, setMatchedProvider] = useState(null);
  const [showPayBillSlider, setShowPayBillSlider] = useState(false);
  const [loadingProvider, setLoadingProvider] = useState(false);

  // Lock body scroll when drawer is open
  useBodyScrollLock(showGroupDealsDrawer && !!selectedGroup);

  // Fetch group details only if deals are not already loaded on selectedGroup
  useEffect(() => {
    if (showGroupDealsDrawer && selectedGroup) {
      // If selectedGroup already has deals from GroupsTab fetch, use those
      if (selectedGroup.deals?.length > 0) {
        console.log('Using deals from selectedGroup:', selectedGroup.deals);
        setGroupDetails(selectedGroup);
        return;
      }

      // Otherwise fetch group details
      const fetchGroupDetails = async () => {
        setLoadingDetails(true);
        try {
          const groupId = selectedGroup.uuid || selectedGroup.id;
          const details = await groupsApi.getOne(groupId);
          console.log('Group Details Response:', details);
          // Log deals structure for debugging
          if (details?.deals?.length > 0) {
            console.log('Sample deal structure:', JSON.stringify(details.deals[0], null, 2));
          }
          setGroupDetails(details);
        } catch (err) {
          console.error('Failed to fetch group details:', err);
        } finally {
          setLoadingDetails(false);
        }
      };
      fetchGroupDetails();
    }
  }, [showGroupDealsDrawer, selectedGroup]);

  if (!showGroupDealsDrawer || !selectedGroup) return null;

  // Use deals from selectedGroup (if already fetched) or from groupDetails
  // Each deal has: uuid, merchant_name, merchant_logo, deal_percentage, etc.
  const deals = selectedGroup.deals?.length > 0 ? selectedGroup.deals : (groupDetails?.deals || []);
  const merchants = selectedGroup.merchants || [];

  // Fallback to mock deals from context for backwards compatibility
  const mockDeals = isolatedGroupDeals[selectedGroup.id] || [];

  // Generate cashback code for a deal
  const handleGenerateCode = async (deal) => {
    // Get group ID - try multiple field names for different group types (public, private, team)
    const groupId = selectedGroup.uuid || selectedGroup.group_uuid || selectedGroup.id;

    // Get the marketplace deal ID from the deal object
    // Different group types may have different deal structures:
    // - Public/Private groups: deal.uuid, deal.marketplace_deal_uuid
    // - Team groups: deal.deal_uuid, deal.marketplace_deal.uuid, deal.id
    const marketplaceDealId =
      deal.uuid ||
      deal.marketplace_deal_uuid ||
      deal.marketplace_deal_id ||
      deal.deal_uuid ||
      deal.marketplace_deal?.uuid ||
      deal.marketplace_deal?.id ||
      deal.deal?.uuid ||
      deal.deal?.id ||
      deal.id;

    // Debug logging - include group type info
    const isTeamGroup = selectedGroup.access_level?.toLowerCase() === 'team' || selectedGroup.isTeam;
    console.log('Generate Code Debug:', {
      deal,
      groupId,
      marketplaceDealId,
      isTeamGroup,
      accessLevel: selectedGroup.access_level,
      selectedGroup: {
        uuid: selectedGroup.uuid,
        group_uuid: selectedGroup.group_uuid,
        id: selectedGroup.id,
        access_level: selectedGroup.access_level,
      },
      dealFields: {
        uuid: deal.uuid,
        marketplace_deal_uuid: deal.marketplace_deal_uuid,
        deal_uuid: deal.deal_uuid,
        id: deal.id,
        marketplace_deal: deal.marketplace_deal,
      },
    });

    if (!groupId) {
      console.error('No group ID found. Group object:', selectedGroup);
      alert('Unable to generate code. Group information not found.');
      return;
    }

    if (!marketplaceDealId) {
      console.error('No marketplace deal ID found. Deal object:', deal);
      alert('Unable to generate code. Deal information not found.');
      return;
    }

    setSelectedDeal(deal);
    setGeneratingCode(true);
    setShowCodeSlider(true);

    try {
      const response = await groupCodeApi.generateCode(groupId, marketplaceDealId);
      console.log('Generate Code Response:', response);
      console.log('Generate Code Response (stringified):', JSON.stringify(response, null, 2));

      // Extract the code from various possible response structures
      // Boldscale may return: { code: "..." }, { discount_code: "..." }, { groupCode: { code: "..." } }, etc.
      let extractedCode = null;

      if (typeof response === 'string') {
        // Response is the code directly
        extractedCode = response;
      } else if (response) {
        // Try various field names that Boldscale might use
        extractedCode =
          response.code ||
          response.discount_code ||
          response.discountCode ||
          response.voucher_code ||
          response.voucherCode ||
          response.groupCode?.code ||
          response.group_code?.code ||
          response.data?.code ||
          response.data?.discount_code ||
          response.data?.discountCode ||
          // If the response has a nested object with the code
          (response.groupCode && typeof response.groupCode === 'string' ? response.groupCode : null) ||
          (response.group_code && typeof response.group_code === 'string' ? response.group_code : null);
      }

      console.log('Extracted code:', extractedCode);
      setGeneratedCode(extractedCode || response);
    } catch (err) {
      console.error('Failed to generate code:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.status,
        body: err.body,
        groupId,
        marketplaceDealId,
        isTeamGroup,
      });

      // Provide more helpful error message based on error type
      let errorMessage = 'Failed to generate code';
      if (err.status === 400) {
        // Bad request - show the server's error message if available
        errorMessage = err.body?.detail || err.body?.message || err.body?.error || 'Invalid request. Please try again.';
      } else if (err.message?.includes('404') || err.status === 404) {
        errorMessage = 'Deal not found. This deal may have expired or is not available.';
      } else if (err.message?.includes('401') || err.message?.includes('403') || err.status === 401 || err.status === 403) {
        errorMessage = 'You do not have permission to access this deal.';
      } else if (err.message?.includes('network') || err.message?.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setGeneratedCode({ error: true, message: errorMessage });
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleCopyCode = async () => {
    // Use the same extraction logic as displayCode
    const extractCode = () => {
      if (!generatedCode) return '';
      if (typeof generatedCode === 'string') return generatedCode;
      return (
        generatedCode.code ||
        generatedCode.discount_code ||
        generatedCode.discountCode ||
        generatedCode.voucher_code ||
        generatedCode.voucherCode ||
        generatedCode.groupCode?.code ||
        generatedCode.group_code?.code ||
        generatedCode.data?.code ||
        (typeof generatedCode.groupCode === 'string' ? generatedCode.groupCode : '') ||
        (typeof generatedCode.group_code === 'string' ? generatedCode.group_code : '') ||
        ''
      );
    };
    const code = extractCode();
    if (code) {
      try {
        await navigator.clipboard.writeText(code);
        setCodeCopied(true);
        setTimeout(() => setCodeCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy code:', err);
      }
    }
  };

  const handleShopNow = async () => {
    if (!selectedDeal) return;

    const merchantName = selectedDeal.merchant_name || selectedDeal.marketplace?.name || selectedDeal.name;
    console.log('Shop Now - Checking merchant_type first:', merchantName);

    setLoadingProvider(true);

    try {
      // Step 1: Get marketplace deal info to check merchant_type
      const dealInfo = await getMarketplaceDealInfo(selectedDeal);
      const rawMerchantType = dealInfo?.merchant_type ?? dealInfo?.merchantType ?? '';
      const merchantType = String(rawMerchantType);
      console.log('Shop Now - merchant_type:', merchantType);

      // Step 2: For retail merchants (type != "1"), open merchant website with code
      if (merchantType !== '1') {
        console.log('Retail merchant - opening website with discount code');
        const merchantWebsite = dealInfo?.merchant_website || dealInfo?.merchantWebsite || '';
        const code = typeof generatedCode === 'string' ? generatedCode :
          (generatedCode?.code || generatedCode?.discount_code || generatedCode?.discountCode || '');

        if (merchantWebsite) {
          // Append code to URL
          const separator = merchantWebsite.includes('?') ? '&' : '?';
          const urlWithCode = code ? `${merchantWebsite}${separator}code=${encodeURIComponent(code)}` : merchantWebsite;
          console.log('Opening merchant website:', urlWithCode);
          window.open(urlWithCode, '_blank');
        } else {
          console.log('No merchant website found, closing drawer');
        }

        setShowCodeSlider(false);
        setLoadingProvider(false);
        return;
      }

      // Step 3: For bill payment merchants (type == "1"), do provider matching
      console.log('Bill payment merchant - looking for provider match');

      // Fetch services to find mobile data, mobile topup, airtime services
      const servicesData = await servicesApi.getAll();

      // Identify featured store services (Mobile Data, Mobile Top Up, Airtime)
      const featuredServices = servicesData.filter(s => {
        const name = s.name?.toLowerCase() || '';
        if (name.includes('utilit')) return false;
        return name.includes('data') ||
               name.includes('top up') ||
               name.includes('topup') ||
               name.includes('airtime');
      });

      // Fetch providers for featured services
      const featuredServiceIds = featuredServices.map(s => s.service_uuid);
      const providerPromises = featuredServiceIds.map(serviceId =>
        providersApi.getByService(serviceId).catch(() => [])
      );

      const providerArrays = await Promise.all(providerPromises);
      const allProviders = providerArrays.flat();

      // Remove duplicates
      const uniqueProviders = Array.from(
        new Map(allProviders.map(p => [p.provider_uuid, p])).values()
      );

      console.log('Fetched providers for matching:', uniqueProviders.length);

      // Try to find a matching provider by name (case-insensitive, flexible matching)
      const merchantLower = merchantName?.toLowerCase().trim() || '';
      const matchedProv = uniqueProviders.find(provider => {
        const providerName = provider.name?.toLowerCase().trim() || '';

        // Direct match
        if (providerName === merchantLower) return true;

        // Provider name contains merchant name or vice versa
        if (providerName.includes(merchantLower) || merchantLower.includes(providerName)) return true;

        // Extract provider name without country prefix (e.g., "Nigeria MTN" -> "MTN")
        const nameParts = provider.name?.split(' ') || [];
        if (nameParts.length > 1) {
          const nameWithoutCountry = nameParts.slice(1).join(' ').toLowerCase();
          if (nameWithoutCountry === merchantLower || merchantLower.includes(nameWithoutCountry)) return true;
        }

        return false;
      });

      if (matchedProv) {
        console.log('Found matching provider:', matchedProv.name, matchedProv.provider_uuid);
        setMatchedProvider(matchedProv);
        setShowCodeSlider(false);
        setShowPayBillSlider(true);
      } else {
        console.log('No matching provider found, falling back to BillingFlow');
        setShowCodeSlider(false);
        setBillingProvider(merchantName);
      }
    } catch (err) {
      console.error('Failed to fetch providers:', err);
      // Fall back to BillingFlow on error
      setShowCodeSlider(false);
      setBillingProvider(merchantName);
    } finally {
      setLoadingProvider(false);
    }
  };

  const handleCloseCodeSlider = () => {
    setShowCodeSlider(false);
    setGeneratedCode(null);
    setSelectedDeal(null);
    setCodeCopied(false);
  };

  // Cache for marketplace deals to avoid repeated API calls
  const [marketplaceDealsCache, setMarketplaceDealsCache] = useState(null);

  // Fetch marketplace deal info to get merchant_type
  const getMarketplaceDealInfo = async (deal) => {
    try {
      // Use cache if available
      let deals = marketplaceDealsCache;
      if (!deals) {
        console.log('Fetching marketplace deals for merchant_type check...');
        deals = await marketplaceDealsApi.getAll();
        setMarketplaceDealsCache(deals);
        console.log(`Cached ${deals.length} marketplace deals`);
      }

      // Try to find matching deal by deal ID first
      const dealId = deal.uuid || deal.marketplace_deal_uuid || deal.deal_uuid || deal.id;
      if (dealId) {
        const matchedDeal = deals.find(d => d.uuid === dealId || d.id === dealId);
        if (matchedDeal) {
          console.log('Found marketplace deal by ID:', matchedDeal.merchant_name);
          return matchedDeal;
        }
      }

      // Fallback to merchant name matching
      const merchantName = deal.merchant_name || deal.marketplace?.name || deal.name || '';
      if (merchantName) {
        const normalizedName = merchantName.toLowerCase().trim();
        const matchedDeal = deals.find(d => {
          const dealMerchant = (d.merchant_name || d.name || '').toLowerCase().trim();
          return dealMerchant === normalizedName ||
                 dealMerchant.includes(normalizedName) ||
                 normalizedName.includes(dealMerchant);
        });
        if (matchedDeal) {
          console.log('Found marketplace deal by name:', matchedDeal.merchant_name);
          return matchedDeal;
        }
      }

      console.log('No marketplace deal info found');
      return null;
    } catch (err) {
      console.error('Failed to fetch marketplace deal info:', err);
      return null;
    }
  };

  // Direct shop now - routes to PayBillSlider immediately, code generation deferred until card payment selected
  const handleDirectShopNow = async (deal) => {
    const merchantName = deal.merchant_name || deal.marketplace?.name || deal.name;
    console.log('Direct Shop Now - Merchant:', merchantName);

    setSelectedDeal(deal);
    setLoadingProvider(true);
    setGeneratedCode(null); // Reset any previous code

    try {
      // Only do provider matching - code generation is deferred to PayBillSlider
      // Fetch services to find mobile data, mobile topup, airtime, bill payment services
      const servicesData = await servicesApi.getAll();

      // Identify featured store services (Mobile Data, Mobile Top Up, Airtime, Bill Payments)
      const featuredServices = servicesData.filter(s => {
        const name = s.name?.toLowerCase() || '';
        return name.includes('data') ||
               name.includes('top up') ||
               name.includes('topup') ||
               name.includes('airtime') ||
               name.includes('cable') ||
               name.includes('tv') ||
               name.includes('electric') ||
               name.includes('power') ||
               name.includes('internet') ||
               name.includes('utility') ||
               name.includes('bill');
      });

      // Fetch providers for featured services
      const featuredServiceIds = featuredServices.map(s => s.service_uuid);
      const providerPromises = featuredServiceIds.map(serviceId =>
        providersApi.getByService(serviceId).catch(() => [])
      );

      const providerArrays = await Promise.all(providerPromises);
      const allProviders = providerArrays.flat();

      // Remove duplicates
      const uniqueProviders = Array.from(
        new Map(allProviders.map(p => [p.provider_uuid, p])).values()
      );

      console.log('Fetched providers for direct matching:', uniqueProviders.length);

      // Try to find a matching provider by name
      const merchantLower = merchantName?.toLowerCase().trim() || '';
      const matchedProv = uniqueProviders.find(provider => {
        const providerName = provider.name?.toLowerCase().trim() || '';

        // Direct match
        if (providerName === merchantLower) return true;

        // Provider name contains merchant name or vice versa
        if (providerName.includes(merchantLower) || merchantLower.includes(providerName)) return true;

        // Extract provider name without country prefix
        const nameParts = provider.name?.split(' ') || [];
        if (nameParts.length > 1) {
          const nameWithoutCountry = nameParts.slice(1).join(' ').toLowerCase();
          if (nameWithoutCountry === merchantLower || merchantLower.includes(nameWithoutCountry)) return true;
        }

        return false;
      });

      if (matchedProv) {
        console.log('Found matching provider:', matchedProv.name, matchedProv.provider_uuid);
        setMatchedProvider(matchedProv);
        setLoadingProvider(false);
        setShowPayBillSlider(true);
        // Note: Code generation is now deferred to PayBillSlider when card payment is selected
      } else {
        // No matching provider found - fall back to code slider flow
        console.log('No matching provider, falling back to code generation flow');
        setLoadingProvider(false);
        handleGenerateCode(deal);
      }
    } catch (err) {
      console.error('Direct shop now failed:', err);
      setLoadingProvider(false);
      // Fall back to code generation flow
      handleGenerateCode(deal);
    }
  };

  // Handle deal click - check merchant_type FIRST, then conditionally route
  // merchant_type == "1" → try provider matching (bill payment flow)
  // merchant_type != "1" → skip provider matching, use code generation (retail flow)
  const handleDealClick = async (deal) => {
    const merchantName = deal.merchant_name || deal.marketplace?.name || deal.name;
    console.log('=== Deal Click - Merchant Type Check First ===');
    console.log('Merchant:', merchantName);

    setSelectedDeal(deal);
    setLoadingProvider(true);

    try {
      // Step 1: Fetch marketplace deal info to get merchant_type
      const dealInfo = await getMarketplaceDealInfo(deal);

      console.log('Marketplace deal info:', dealInfo ? 'found' : 'not found');
      if (dealInfo) {
        console.log('  merchant_type:', dealInfo.merchant_type, '(type:', typeof dealInfo.merchant_type, ')');
        console.log('  merchantType:', dealInfo.merchantType, '(type:', typeof dealInfo.merchantType, ')');
        console.log('  merchant_website:', dealInfo.merchant_website);
      }

      // Step 2: Check merchant_type to determine flow
      // merchant_type == "1" → bill payment providers (MTN, Airtel, etc.)
      // merchant_type != "1" → retail/e-commerce (Merrylinks, etc.)
      // Handle both snake_case and camelCase, and both string and number types
      const rawMerchantType = dealInfo?.merchant_type ?? dealInfo?.merchantType ?? '';
      const merchantType = String(rawMerchantType);
      console.log('Resolved merchantType:', merchantType);

      if (merchantType !== '1') {
        // Retail/e-commerce flow - skip provider matching, go to code generation
        console.log(`merchant_type != "1" (${merchantType}) → Using code generation flow`);
        setLoadingProvider(false);
        handleGenerateCode(deal);
        return;
      }

      // Step 3: Only for merchant_type == "1" - try provider matching
      console.log('merchant_type == "1" → Trying provider matching');
      setLoadingProvider(false);
      handleDirectShopNow(deal);
    } catch (err) {
      console.error('Deal click flow failed:', err);
      setLoadingProvider(false);
      // Fall back to code generation on error
      handleGenerateCode(deal);
    }
  };

  const handleBillingClose = () => {
    setBillingProvider(null);
    setShowGroupDealsDrawer(false);
  };

  // Determine if this is a billing/utilities group
  const groupName = selectedGroup.name?.toLowerCase() || '';
  const groupDesc = selectedGroup.description?.toLowerCase() || '';
  const isBillingGroup = groupName.includes('internet') || groupName.includes('bill') ||
    groupName.includes('utility') || groupDesc.includes('internet') || groupDesc.includes('topup');

  // Get display code - extract from various possible response structures
  const getDisplayCode = () => {
    if (!generatedCode) return '';
    if (typeof generatedCode === 'string') return generatedCode;
    // Try various field names
    return (
      generatedCode.code ||
      generatedCode.discount_code ||
      generatedCode.discountCode ||
      generatedCode.voucher_code ||
      generatedCode.voucherCode ||
      generatedCode.groupCode?.code ||
      generatedCode.group_code?.code ||
      generatedCode.data?.code ||
      (typeof generatedCode.groupCode === 'string' ? generatedCode.groupCode : '') ||
      (typeof generatedCode.group_code === 'string' ? generatedCode.group_code : '') ||
      ''
    );
  };
  const displayCode = getDisplayCode();

  return (
    <>
      {/* Backdrop */}
      {/* Transparent backdrop - GroupChatDrawer already provides visible backdrop, this just catches clicks */}
      <div className="fixed inset-0 z-[90]" onClick={() => setShowGroupDealsDrawer(false)} />

      {/* Full Screen Panel */}
      <div className="slide-panel">
        <div>
          {/* Header - Fixed */}
          <div className="slide-panel-header flex items-center gap-3 p-4 border-b border-gray-100">
            <button
              onClick={() => setShowGroupDealsDrawer(false)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h2 className="text-lg font-bold">Active Deals</h2>
              <p className="text-xs text-gray-500 font-medium">Curated exclusively for {selectedGroup.name}</p>
            </div>
          </div>

          {/* Content - Scrollable */}
          <div className="slide-panel-content p-4 space-y-3">
            {/* Loading state */}
            {loadingDetails && (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={32} className="animate-spin text-indigo-600" />
              </div>
            )}

            {/* Show deals from group details API */}
            {!loadingDetails && deals.length > 0 ? (
              deals.map((deal, index) => {
                // Get merchant info from the deal
                const merchantName = deal.merchant_name || deal.marketplace?.name || deal.name || 'Merchant';
                const merchantLogo = deal.merchant_logo || deal.marketplace?.logo || deal.logo;
                const cashbackPercent = deal.deal_percentage || deal.dealPercentage || deal.cashback || deal.discount || null;

                return (
                  <div
                    key={deal.uuid || deal.marketplace_deal_uuid || deal.id || index}
                    onClick={() => !loadingProvider && handleDealClick(deal)}
                    className={`flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-50 hover:border-indigo-600 transition-all bg-white shadow-sm active:scale-[0.98] ${loadingProvider ? 'cursor-wait' : 'cursor-pointer'}`}
                  >
                    {/* Merchant Logo */}
                    {merchantLogo ? (
                      <img
                        src={merchantLogo}
                        alt={merchantName}
                        className="w-12 h-12 rounded-xl object-contain shadow-md bg-white p-1 border border-gray-100"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className={`w-12 h-12 bg-indigo-600 rounded-xl ${merchantLogo ? 'hidden' : 'flex'} items-center justify-center text-white font-bold shadow-md`}
                    >
                      {merchantName?.substring(0, 2).toUpperCase() || 'M'}
                    </div>

                    {/* Deal Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-black text-gray-900">{merchantName}</h4>
                      <span className="inline-block mt-1 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-[10px] font-bold">
                        {cashbackPercent ? `${cashbackPercent}% Cashback` : 'Earn Cashback'}
                      </span>
                    </div>

                    {/* Get Cashback Button - Shows loading when this deal is being processed */}
                    {loadingProvider && selectedDeal && (selectedDeal.uuid === deal.uuid || selectedDeal.merchant_name === (deal.merchant_name || deal.marketplace?.name || deal.name)) ? (
                      <div className="px-3 py-2 bg-indigo-600 text-white text-xs font-bold rounded-lg whitespace-nowrap flex items-center gap-1.5">
                        <Loader2 size={14} className="animate-spin" />
                        <span>Loading</span>
                      </div>
                    ) : (
                      <button className="px-3 py-2 bg-indigo-600 text-white text-xs font-bold rounded-lg whitespace-nowrap">
                        Shop Now
                      </button>
                    )}
                  </div>
                );
              })
            ) : !loadingDetails && merchants.length > 0 ? (
              // Fallback to merchants from selectedGroup if no deals in details
              merchants.map((merchant, index) => {
                const cashbackPercent = merchant.cashback || merchant.cashback_percent ||
                  merchant.deal_percent || merchant.discount || null;

                return (
                  <div
                    key={merchant.merchant_name || index}
                    onClick={() => !loadingProvider && handleDealClick({
                      merchant_name: merchant.merchant_name,
                      merchant_logo: merchant.merchant_logo,
                      // Note: Without deal ID from group details, code generation may fail
                    })}
                    className={`flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-50 hover:border-indigo-600 transition-all bg-white shadow-sm active:scale-[0.98] ${loadingProvider ? 'cursor-wait' : 'cursor-pointer'}`}
                  >
                    <img
                      src={merchant.merchant_logo}
                      alt={merchant.merchant_name}
                      className="w-12 h-12 rounded-xl object-contain shadow-md bg-white p-1 border border-gray-100"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div
                      className="w-12 h-12 bg-indigo-600 rounded-xl hidden items-center justify-center text-white font-bold shadow-md"
                    >
                      {merchant.merchant_name?.substring(0, 2).toUpperCase() || 'M'}
                    </div>

                    <div className="flex-1 min-w-0">
                      <h4 className="font-black text-gray-900">{merchant.merchant_name}</h4>
                      <span className="inline-block mt-1 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-[10px] font-bold">
                        {cashbackPercent ? `${cashbackPercent}% Cashback` : 'Earn Cashback'}
                      </span>
                    </div>

                    {/* Get Cashback Button - Shows loading when this merchant is being processed */}
                    {loadingProvider && selectedDeal && selectedDeal.merchant_name === merchant.merchant_name ? (
                      <div className="px-3 py-2 bg-indigo-600 text-white text-xs font-bold rounded-lg whitespace-nowrap flex items-center gap-1.5">
                        <Loader2 size={14} className="animate-spin" />
                        <span>Loading</span>
                      </div>
                    ) : (
                      <button className="px-3 py-2 bg-indigo-600 text-white text-xs font-bold rounded-lg whitespace-nowrap">
                        Shop Now
                      </button>
                    )}
                  </div>
                );
              })
            ) : !loadingDetails && mockDeals.length > 0 ? (
              // Fallback to mock deals for backwards compatibility
              mockDeals.map((deal) => (
                <div
                  key={deal.id}
                  onClick={() => {
                    setSelectedDeal({ merchant_name: deal.brand, merchant_logo: null });
                    setShowCodeSlider(true);
                    setGeneratedCode({ code: 'MOCK-CODE-123' });
                  }}
                  className="flex items-center gap-4 p-4 rounded-2xl border-2 border-gray-50 hover:border-indigo-600 cursor-pointer transition-all bg-white shadow-sm active:scale-[0.98]"
                >
                  <div className={`w-12 h-12 ${deal.color || 'bg-indigo-600'} rounded-xl flex items-center justify-center text-white font-bold shadow-md`}>
                    {deal.logo}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-black text-gray-900">{deal.brand}</h4>
                    <span className="inline-block mt-1 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-[10px] font-bold">
                      {deal.offer}
                    </span>
                  </div>
                  <button className="px-3 py-2 bg-indigo-600 text-white text-xs font-bold rounded-lg whitespace-nowrap">
                    Get Cashback
                  </button>
                </div>
              ))
            ) : !loadingDetails ? (
              // Empty state
              <div className="text-center py-8">
                <p className="text-gray-400 mb-4">No active deals in this group</p>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Generated Code Slider - Full screen */}
      {showCodeSlider && (
        <>
          {/* Transparent backdrop - parent drawer already provides visible backdrop */}
          <div className="fixed inset-0 z-[90]" onClick={handleCloseCodeSlider} />
          <div className="slide-panel">
            <div>
              {/* Header - Fixed */}
              <div className="slide-panel-header flex items-center justify-between p-4 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900">Your Cashback Code</h2>
                <button
                  onClick={handleCloseCodeSlider}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Content - Centered */}
              <div className="slide-panel-content flex flex-col items-center justify-center p-6">
                {generatingCode ? (
                  <div className="text-center">
                    <Loader2 size={48} className="animate-spin text-indigo-600 mx-auto mb-4" />
                    <p className="text-lg text-gray-500 font-medium">Generating your code...</p>
                    <p className="text-sm text-gray-400 mt-2">This may take a moment</p>
                  </div>
                ) : generatedCode?.error ? (
                  <div className="text-center">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <X size={40} className="text-red-500" />
                    </div>
                    <p className="text-lg text-red-500 font-bold mb-2">Oops!</p>
                    <p className="text-sm text-gray-500">{generatedCode.message || 'Failed to generate code'}</p>
                  </div>
                ) : (
                  <div className="text-center w-full">
                    {/* Merchant Logo */}
                    <div className="mb-6">
                      {(selectedDeal?.merchant_logo || selectedDeal?.marketplace?.logo || selectedDeal?.logo) ? (
                        <img
                          src={selectedDeal.merchant_logo || selectedDeal.marketplace?.logo || selectedDeal.logo}
                          alt={selectedDeal.merchant_name || selectedDeal.marketplace?.name || selectedDeal.name}
                          className="w-20 h-20 rounded-2xl object-contain shadow-lg bg-white p-2 border-2 border-indigo-100 mx-auto"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-lg mx-auto">
                          {(selectedDeal?.merchant_name || selectedDeal?.marketplace?.name || selectedDeal?.name || 'D')?.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                    </div>

                    {/* Success Message */}
                    <p className="text-sm text-gray-500 mb-1">Your cashback code for</p>
                    <p className="text-lg font-bold text-gray-900 mb-4">
                      {selectedDeal?.merchant_name || selectedDeal?.marketplace?.name || selectedDeal?.name}
                    </p>

                    {/* Generated Code Display */}
                    <div className="mb-6">
                      {displayCode ? (
                        <p className="text-3xl font-black text-indigo-600 tracking-widest break-all">
                          {displayCode}
                        </p>
                      ) : (
                        <div className="text-amber-600">
                          <p className="text-lg font-bold">Code generated!</p>
                          <p className="text-xs mt-1">Check console for response structure</p>
                        </div>
                      )}
                    </div>

                    {/* Buttons */}
                    <div className="space-y-3">
                      <button
                        onClick={handleCopyCode}
                        className="w-full py-3 bg-white border-2 border-indigo-600 text-indigo-600 rounded-xl font-bold hover:bg-indigo-50 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                      >
                        {codeCopied ? (
                          <>
                            <Check size={20} className="text-emerald-500" />
                            <span className="text-emerald-500">Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy size={20} />
                            <span>Copy Code</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleShopNow}
                        disabled={loadingProvider}
                        className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 active:scale-[0.98] transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-70"
                      >
                        {loadingProvider ? (
                          <>
                            <Loader2 size={18} className="animate-spin" />
                            <span>Loading...</span>
                          </>
                        ) : (
                          <>
                            <span>Shop Now</span>
                            <ChevronRight size={18} />
                          </>
                        )}
                      </button>
                    </div>

                    {/* Footer hint */}
                    <p className="text-xs text-gray-400 mt-4">Use this code at checkout to earn cashback</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Billing Flow */}
      {billingProvider && (
        <BillingFlow
          provider={billingProvider}
          onClose={handleBillingClose}
        />
      )}

      {/* Pay Bill Slider - When merchant matches a Featured Store provider */}
      {showPayBillSlider && matchedProvider && (
        <PayBillSlider
          provider={matchedProvider}
          hideBackdrop
          cashbackPercent={selectedDeal?.deal_percentage || selectedDeal?.dealPercentage || selectedDeal?.cashback || selectedDeal?.discount || null}
          merchantLogo={selectedDeal?.merchant_logo || selectedDeal?.marketplace?.logo || selectedDeal?.logo}
          // Pass deal info for deferred code generation when card payment is selected
          dealGroupId={selectedGroup?.uuid || selectedGroup?.group_uuid || selectedGroup?.id}
          dealMarketplaceId={
            selectedDeal?.uuid ||
            selectedDeal?.marketplace_deal_uuid ||
            selectedDeal?.marketplace_deal_id ||
            selectedDeal?.deal_uuid ||
            selectedDeal?.marketplace_deal?.uuid ||
            selectedDeal?.marketplace_deal?.id ||
            selectedDeal?.deal?.uuid ||
            selectedDeal?.deal?.id ||
            selectedDeal?.id
          }
          onClose={() => {
            setShowPayBillSlider(false);
            setMatchedProvider(null);
            setGeneratedCode(null);
            setShowGroupDealsDrawer(false);
          }}
        />
      )}
    </>
  );
}
